// src/pages/products/StudioToolsPage.jsx
import { motion } from "framer-motion";
import toolsIcon from "../../assets/portfolio/coming-soon.png";

export default function StudioToolsPage() {
  return (
    <div className="w-full bg-transparent text-black pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-20">

        {/* ================= HERO ================= */}
        <section className="flex flex-col items-center gap-10 text-center">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="hero-heading mb-4">
              Code7s Studio Tools
            </h1>

            <p className="hero-copy">
              A real-time product prototyping and wireframing platform built
              to help founders and teams visualize software products before
              development begins.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <img
              src={toolsIcon}
              alt="Code7s Studio Tools"
              className="
                w-64 h-64 object-contain
                drop-shadow-[0_25px_35px_rgba(0,0,0,0.15)]
              "
            />
          </motion.div>

        </section>

        {/* ================= PROJECT OVERVIEW ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="section-heading mb-6"
          >
            Product Overview
          </motion.h2>

          <p className="section-copy">
            Code7s Studio Tools was created to solve a common problem faced by
            founders and teams: jumping into development without clearly
            understanding the product structure, flow, and scope.
          </p>

          <p className="section-copy mt-6">
            The platform allows users to prototype screens, wireframe flows,
            and visualize how a software product will function in real time.
            This makes it easier to align ideas, reduce uncertainty, and
            make better decisions before committing engineering resources.
          </p>
        </section>

        {/* ================= OUR STRATEGY ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <h2 className="section-heading mb-6">
            Our Strategy
          </h2>

          <p className="section-copy">
            The strategy behind Studio Tools was to bridge the gap between
            abstract ideas and production-ready software. Instead of focusing
            purely on visuals, the tool emphasizes structure, flow, and
            real-world feasibility.
          </p>

          <p className="section-copy mt-6">
            By enabling early visualization and iteration, Studio Tools helps
            teams identify gaps, refine concepts, and validate assumptions
            long before development begins.
          </p>
        </section>

        {/* ================= WHAT WE BUILT ================= */}
        <section className="mt-32">
          <h2 className="section-heading mb-10">
            What We Built
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              "Real time prototyping canvas",
              "Wireframing & screen layout tools",
              "Product flow visualization",
              "Early scope & feature mapping",
              "Collaboration ready structure",
              "Foundational system for future expansion",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="
                  glass-card rounded-[22px] p-6
                "
              >
                <h3 className="text-[15px] font-medium">{item}</h3>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= WHO IT’S FOR ================= */}
        <section className="mx-auto mt-32 max-w-6xl text-center">
          <h2 className="section-heading mb-10">
            Who It’s For
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              "Startup founders planning MVPs",
              "Product managers defining scope",
              "Designers prototyping user flows",
              "Developers planning architecture",
              "Teams validating ideas early",
              "Agencies presenting concepts",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="
                  glass-card rounded-[22px] p-6
                "
              >
                <p className="text-[15px] font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= CTA ================= */}
        <section className="mt-32 text-center">
          <h2 className="section-heading mb-6">
            Prototype Before You Build
          </h2>

          <p className="mx-auto mb-8 max-w-xl text-center text-neutral-600">
            Turn ideas into clear product structures before investing
            time and money into development.
          </p>

          <a
            href="/contact"
            className="
              inline-block px-10 py-3 rounded-full
              bg-black text-white font-medium
              hover:bg-opacity-80 transition
            "
          >
            Get Early Access
          </a>
        </section>

      </div>
    </div>
  );
}
